<template>
  <Loader :value="promise" theme="overlay" class="integration-webmegler-units">
    <table>
      <tr>
        <th>Oppdrag i Webmegler</th>
        <th>Enhet i Kvass</th>
        <th>Status</th>
      </tr>
      <tbody>
        <tr v-for="item in webmegler" :key="item.value">
          <td>
            <strong v-if="item.label" class="integration-webmegler-units__public">{{ item.label }}</strong>
            <span v-if="item.sublabel" class="integration-webmegler-units__internal">{{ item.sublabel }}</span>
          </td>
          <td>
            <select :value="value.map[item.value]" :disabled="disabled" @input="$ev => setMap(item, $ev)">
              <option :value="null">Opprett ny enhet</option>
              <option :value="false">Hopp over</option>
              <option
                v-for="kitem in getKvassUnits(item.value)"
                :key="kitem.value"
                :value="kitem.value"
                :disabled="isUsed(kitem.value, item.value)"
              >
                {{ kitem.label }}
              </option>
            </select>
          </td>
          <td>
            <span
              :class="[
                'integration-webmegler-units__status',
                `integration-webmegler-units__status--${getStatus(item.value).key}`,
              ]"
            >
              <Icon :icon="getStatus(item.value).icon" />
              {{ getStatus(item.value).label }}
            </span>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td colspan="3">
            <div class="integration-webmegler-units__counters">
              <span v-for="status in statusCounts" :key="status.key">{{ status.label }}: {{ status.count }}</span>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  </Loader>
</template>

<script>
import { LoaderComponent as Loader } from 'vue-elder-loader'
import { request } from '@/api/request'
import Icon from '@kvass/vue2-icon'

const Status = {
  synced: { label: 'Oppkoblet', icon: 'fa-pro-solid:check' },
  pending: { label: 'Opprettes', icon: 'fa-pro-solid:plus' },
  skipped: { label: 'Frakoblet', icon: 'fa-pro-solid:link-slash' },
}

function convertTarget(val) {
  switch (val) {
    case 'false':
      return false
    case '':
      return null
    default:
      return val
  }
}

export default {
  props: {
    id: String,
    value: Object,
    disabled: Boolean,
  },
  inject: ['account', 'token', 'integration'],
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      promise: null,
      webmegler: [],
      kvass: [],
    }
  },
  computed: {
    usedUnits() {
      return Object.values(this.value.map || {}).filter(Boolean)
    },
    statusCounts() {
      return Object.entries(Status).map(([key, status]) => ({
        ...status,
        key,
        count: this.webmegler.filter(({ value }) => this.getStatus(value).key === key).length,
      }))
    },
  },
  methods: {
    getStatus(id) {
      const value = (this.value.map || {})[id]
      if (value === false) return { key: 'skipped', ...Status.skipped }
      if (value) return { key: 'synced', ...Status.synced }
      return { key: 'pending', ...Status.pending }
    },
    isUsed(kvassId, id) {
      return this.value.map[id] !== kvassId && this.usedUnits.includes(kvassId)
    },
    getKvassUnits(id) {
      return [...this.kvass].sort((a, b) => Number(this.isUsed(a.value, id)) - Number(this.isUsed(b.value, id)))
    },
    fetch() {
      const id = this.id

      this.webmegler = []
      this.$emit('input', { ...this.value, map: null })

      this.promise = request(`https://${this.account}/api/integration/${this.integration}/callbacks/map?id=${id}`, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      })
        .then(res => res.json())
        .then(({ map, kvass, webmegler }) => {
          if (id !== this.id) return

          this.$emit('input', { ...this.value, map })

          this.kvass = kvass
          this.webmegler = webmegler
        })
        .catch(err => {
          if (id !== this.id) return
          this.$emit('error', err)
        })
    },
    setMap(item, event) {
      let value = convertTarget(event.target.value)
      this.$emit('input', { ...this.value, map: { ...this.value.map, [item.value]: value } })
    },
  },
  components: {
    Loader,
    Icon,
  },
}
</script>

<style lang="scss">
.integration-webmegler-units {
  table {
    border-collapse: collapse;
    width: 100%;
    th,
    td {
      padding: 0.75rem;
      text-align: left;
    }

    tbody tr:nth-child(odd) {
      background-color: hsla(var(--border-color-h), var(--border-color-s), var(--border-color-l), 0.3);
    }

    tfoot td {
      position: sticky;
      bottom: 0;
      padding: 1rem 1.5rem;
      background-color: white;
      border-top: 1px solid #eaeaea;
    }
  }

  &__public,
  &__internal {
    display: block;
  }

  &__internal {
    font-size: 0.8125rem;
    opacity: 0.7;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4em 0.7em;
    border-radius: 30px;
    font-size: 1rem;
    line-height: 1;
    letter-spacing: 0.02rem;
    white-space: nowrap;

    &--synced {
      background-color: #cdf8de;
      color: #075718;
    }

    &--pending {
      background-color: #fff5d6;
      color: #80570c;
    }

    &--skipped {
      background-color: #f0f0f0;
      color: #000000;
      opacity: 0.6;
    }
  }

  &__counters {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    font-size: 1rem;
  }
}
</style>
